import { useTranslation } from "@/hooks/useTranslation";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FluidImage from "../images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import { useRouter } from "next/router";
import { ChevronDown } from "lucide-react";
import { languages } from "@/lib/constants";

function LogoLoader() {
  return (
    <div className="animate-pulse">
      <div className="w-8 h-8 bg-gray-200 rounded"></div>
    </div>
  );
}

function LanguageLoader() {
  return (
    <div className="animate-pulse">
      <div className="w-20 h-8 bg-gray-200 rounded"></div>
    </div>
  );
}

export default function Header({
  isLoading,
  companyLogoUrl,
  handleLanguageChange,
  shopUrl,
  companyName,
  showBackButton,
  backButtonDestination,
}: {
  isLoading: boolean;
  companyLogoUrl?: string;
  handleLanguageChange?: (language: string) => void;
  shopUrl?: string;
  companyName?: string;
  showBackButton?: boolean;
  backButtonDestination?: string;
}) {
  const { t, language, changeLanguage } = useTranslation();
  const router = useRouter();

  const handleBackClick = () => {
    if (backButtonDestination) {
      router.push(backButtonDestination);
    } else {
      router.push("/manage");
    }
  };

  return (
    <div className="bg-white lg:border-b border-gray-200 lg:sticky lg:top-0 z-50">
      <div className="mx-auto flex flex-row justify-between items-center h-[6vh] py-3 px-7 md:py-6">
        {showBackButton && (
          <button
            className="flex items-center px-3 py-2 rounded-lg text-sm hover:bg-gray-50"
            onClick={handleBackClick}
          >
            <FluidImage
              src={`${FLUID_CDN_URL}/images/checkout/icons/arrow-left.svg`}
              alt="Back"
              width={12}
              height={12}
              className="mr-1"
            />
            <span className="text-xs">{t("back")}</span>
          </button>
        )}
        <a href={shopUrl || ""} className="flex items-center">
          <span className="sr-only">{companyName ?? t("your_company")}</span>
          {isLoading ? (
            <LogoLoader />
          ) : companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt="Company Logo"
              width={100}
              height={30}
              className="rounded max-h-[30px] max-w-[100px] object-contain"
            />
          ) : companyName ? (
            <span className="text-lg font-bold">{companyName}</span>
          ) : null}
        </a>
        {isLoading ? (
          <LanguageLoader />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="bg-gray-100 text-left px-3 py-2 rounded text-xs flex items-center justify-between min-w-[100px]">
                <span>
                  {languages?.find((lang) => lang.iso === language)?.name ||
                    "English"}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-40 z-[9999]">
              {languages?.map((lang) => (
                <DropdownMenuItem
                  key={lang.iso}
                  className={`cursor-pointer px-3 py-2 text-xs ${
                    language === lang.iso
                      ? "bg-black text-white font-medium"
                      : "text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    changeLanguage(lang.iso);
                    handleLanguageChange?.(lang.iso);
                  }}
                >
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
